import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { membersAPI } from '../../utils/api';
import { toInputDate, calcExpiryDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';

function EditMember() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    membersAPI.getOne(id).then(res => {
      const m = res.data.member;
      setForm({
        name: m.name || '',
        phone: m.phone || '',
        age: m.age || '',
        gender: m.gender || 'Male',
        plan: m.plan || 'Monthly',
        shift: m.shift || 'Morning',
        joiningDate: toInputDate(m.joiningDate),
        expiryDate: toInputDate(m.expiryDate),
        emergencyName: m.emergencyContact?.name || '',
        emergencyPhone: m.emergencyContact?.phone || '',
        healthNotes: m.healthNotes || '',
        photo: m.photo || '',
      });
      setLoading(false);
    }).catch(() => {
      toast.error('Failed to load member');
      navigate('/members');
    });
  }, [id]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handlePlanChange = e => {
    const plan = e.target.value;
    const expiry = form.joiningDate ? calcExpiryDate(form.joiningDate, plan).toISOString().split('T')[0] : '';
    setForm(f => ({ ...f, plan, expiryDate: expiry }));
  };

  const handlePhoto = e => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.phone && !/^\d{10}$/.test(form.phone)) { toast.error('Phone number must be exactly 10 digits'); return; }
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k !== 'emergencyName' && k !== 'emergencyPhone' && k !== 'photo') formData.append(k, v);
      });
      formData.append('emergencyContact', JSON.stringify({ name: form.emergencyName, phone: form.emergencyPhone }));
      if (photo) formData.append('photo', photo);

      await membersAPI.update(id, formData);
      toast.success('Member updated successfully!');
      navigate('/members');
    } catch {
      toast.error('Failed to update member');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!form) return null;

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/members')} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600">
          ← Back
        </button>
        <h2 className="text-2xl font-bold text-gray-800">Edit Member</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Photo */}
        <div className="card">
          <h3 className="font-semibold text-gray-700 mb-4">Profile Photo</h3>
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300">
              {photoPreview ? (
                <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
              ) : form.photo ? (
                <img src={`${form.photo}`} alt="Current" className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl">📷</span>
              )}
            </div>
            <label className="btn-secondary cursor-pointer text-sm">
              Change Photo
              <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
            </label>
          </div>
        </div>

        {/* Personal */}
        <div className="card">
          <h3 className="font-semibold text-gray-700 mb-4">Personal Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Full Name *</label>
              <input className="input" name="name" value={form.name} onChange={handleChange} required />
            </div>
            <div>
              <label className="label">Phone *</label>
              <input className="input" name="phone" value={form.phone} onChange={handleChange} required />
            </div>
            <div>
              <label className="label">Age *</label>
              <input className="input" type="number" name="age" value={form.age} onChange={handleChange} required />
            </div>
            <div>
              <label className="label">Gender</label>
              <select className="input" name="gender" value={form.gender} onChange={handleChange}>
                <option>Male</option>
                <option>Female</option>
                <option>Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Membership */}
        <div className="card">
          <h3 className="font-semibold text-gray-700 mb-4">Membership Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Plan</label>
              <select className="input" name="plan" value={form.plan} onChange={handlePlanChange}>
                <option>Monthly</option>
                <option>Quarterly</option>
                <option>Yearly</option>
              </select>
            </div>
            <div>
              <label className="label">Shift</label>
              <select className="input" name="shift" value={form.shift} onChange={handleChange}>
                <option>Morning</option>
                <option>Evening</option>
                <option>Both</option>
              </select>
            </div>
            <div>
              <label className="label">Joining Date</label>
              <input className="input" type="date" name="joiningDate" value={form.joiningDate} onChange={handleChange} />
            </div>
            <div>
              <label className="label">Expiry Date</label>
              <input className="input" type="date" name="expiryDate" value={form.expiryDate} onChange={handleChange} />
            </div>
          </div>
        </div>

        {/* Emergency */}
        <div className="card">
          <h3 className="font-semibold text-gray-700 mb-4">Emergency Contact</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Name</label>
              <input className="input" name="emergencyName" value={form.emergencyName} onChange={handleChange} />
            </div>
            <div>
              <label className="label">Phone</label>
              <input className="input" name="emergencyPhone" value={form.emergencyPhone} onChange={handleChange} />
            </div>
          </div>
        </div>

        {/* Health */}
        <div className="card">
          <h3 className="font-semibold text-gray-700 mb-4">Health Notes</h3>
          <textarea className="input resize-none" name="healthNotes" rows={3} value={form.healthNotes} onChange={handleChange} />
        </div>

        <div className="flex gap-3 justify-end">
          <button type="button" onClick={() => navigate('/members')} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary min-w-[120px]">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditMember;
